import { memo, useMemo, useContext } from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import tw from "twin.macro";
import { LINKS } from "globalConfig";
import { makeUrl } from "utils";
import { AdminContext } from "contexts/AdminProvider";
import LiteratiLogoDark from "@/atoms/Icon/literati-dark";

export const LEGAL_LINKS = [
  { label: "Terms of Service", to: LINKS.terms },
  { label: "Privacy", to: LINKS.privacy },
];

const StyledFooter = styled.footer`
  ${tw`p-6`}
  background-color: #fff;

  .content-container {
    ${tw`bg-gray1`}
    border-radius: 12px;
  }

  a {
    ${tw`no-underline text-textGray`}
    &:hover {
      ${tw`underline`}
    }
  }

  .legal {
    ${tw`mt-2 flex flex-wrap`}
    a {
      ${tw`block`}
      &:first-child {
        ${tw`mr-8`}
      }
    }
  }

  .copyright {
    ${tw`flex flex-wrap mt-2`}
    div:first-child {
      ${tw`mr-1`}
    }
  }

  .content {
    ${tw`flex flex-wrap py-8 justify-between`}

    .col {
      white-space: nowrap;
    }

    .logo,
    .col:not(:last-child) {
      ${tw`pr-8`}
    }

    .logo,
    .col-contact {
      ${tw`mb-8`}
    }

    .links {
      ${tw`flex flex-wrap justify-between`}
      flex-grow: 1;
    }

    ${({ theme }) => theme.media["md"]} {
      .logo {
        flex-grow: 3;
      }
      .col:last-child {
        margin-left: auto;
      }
    }
  }
`;

/**
 * Footer for the funnel checkout page.
 *
 * @param {Object} props
 * @param {Boolean} props.isWithinPhoneHours
 * @param {Object} props.contactPhone
 * @param {String|Number} props.contactPhone.number
 * @param {String} props.contactPhone.word
 */
const CheckoutFooter = memo(({ isWithinPhoneHours, contactPhone }) => {
  const currentYear = useMemo(() => {
    const date = new Date();
    return date.getFullYear();
  }, []);

  return (
    <StyledFooter>
      <div className={"content-container"}>
        <div className="content content-spacing">
          <div className="logo">
            <LiteratiLogoDark width={130} />
          </div>
          <div className={"links"}>
            {isWithinPhoneHours && (
              <div className="col col-contact">
                <div className="contact">
                  <p className="mt-2">Have questions? Call us!</p>
                  <a href={`tel:+1-${contactPhone.number}`}>
                    {contactPhone.word}
                  </a>
                  <br />
                  <a href={`tel:+1-${contactPhone.number}`}>
                    {contactPhone.number}
                  </a>
                  <p>Weekdays 9am to 5pm CT</p>
                </div>
              </div>
            )}
            <div className="col col-legal">
              <div className="legal mt-2">
                {LEGAL_LINKS.map((link) => (
                  <a href={makeUrl(link.to)} key={`legal-${link.to}`}>
                    {link.label}
                  </a>
                ))}
              </div>
              <div className="copyright">
                <div>©{currentYear} Literati Inc.</div>
                <div>All Rights Reserved.</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </StyledFooter>
  );
});

CheckoutFooter.propTypes = {
  isWithinPhoneHours: PropTypes.bool,
  contactPhone: PropTypes.shape({
    number: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    word: PropTypes.string,
  }),
};

const ConnectedCheckoutFooter = (props) => {
  const { settings } = useContext(AdminContext);
  const isWithinPhoneHours = settings && settings["WITHIN_PHONE_HOURS"];
  const { CONTACT_PHONE_NUMBER, CONTACT_PHONE_WORD } = settings;
  const contactPhone = {
    number: CONTACT_PHONE_NUMBER,
    word: CONTACT_PHONE_WORD,
  };
  return (
    <CheckoutFooter
      {...props}
      isWithinPhoneHours={isWithinPhoneHours}
      contactPhone={contactPhone}
    />
  );
};

export default ConnectedCheckoutFooter;
